import { Stepper } from '@recipes/ui';
import { useTranslation } from 'react-i18next';
import { formatFraction } from '@recipes/domain';
import { currentLocale } from '@/lib/i18n';

export interface ServingsStepperProps {
  /** Current servings as decimal string. */
  value: string;
  onChange: (value: string) => void;
  /** Original recipe servings; shown as "reset" hint when different. */
  original?: string;
  label?: string | null;
  size?: 'default' | 'lg';
  min?: number;
  max?: number;
  className?: string;
}

/** Stepper for the servings count. Emits canonical decimal strings ("4", "1.5"). */
export function ServingsStepper({
  value,
  onChange,
  original,
  label,
  size = 'default',
  min = 0.5,
  max = 200,
  className,
}: ServingsStepperProps) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const numeric = Number(value);
  const unitLabel = label?.trim() || t('common.servings');
  return (
    <div className={className}>
      <Stepper
        value={Number.isFinite(numeric) && numeric > 0 ? numeric : 1}
        onChange={(n) => onChange(String(Math.round(n * 2) / 2))}
        min={min}
        max={max}
        step={numeric < 2 ? 0.5 : 1}
        size={size}
        label={unitLabel}
        decreaseLabel={t('recipes.servings.decrease')}
        increaseLabel={t('recipes.servings.increase')}
        formatValue={(n) => `${formatFraction(String(n), { locale })} ${unitLabel}`}
      />
      {original !== undefined && original !== value && (
        <button
          type="button"
          className="mt-1 text-xs text-primary underline-offset-4 hover:underline"
          onClick={() => onChange(original)}
          data-testid="servings-reset"
        >
          {t('recipes.servings.reset', { count: Number(original) })}
        </button>
      )}
    </div>
  );
}
